import { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSWRConfig } from 'swr'
import { useSnackbar } from 'notistack'
import api from 'utils/api'
import { AuthPopupEnum } from 'utils/constants'
import * as uiActions from 'modules/UI/actions'
import * as userActions from 'modules/User/actions'
import { removeCachedFavorite, revalidateFavoriteOffers } from './cache'

const errorMessage = (error) => {
  if (error?.message) return error.message
  if (typeof error?.error === 'string') return error.error
  return 'Your favorite could not be updated. Try again.'
}

const useFavoriteOffer = (offerId, { disabled = false, revalidateList } = {}) => {
  const dispatch = useDispatch()
  const { mutate } = useSWRConfig()
  const { enqueueSnackbar } = useSnackbar()
  const userDetails = useSelector((state) => state.user.details)
  const favorites = useSelector((state) => state.user.favorites)
  const isFavorite = Array.isArray(favorites) && favorites.includes(offerId)
  const [isUpdating, setIsUpdating] = useState(false)
  const pendingRef = useRef(false)

  const toggleFavorite = useCallback(async () => {
    if (disabled || userDetails?.lifecycleStatus === 'SUSPENDED' || pendingRef.current) return
    if (!userDetails) {
      dispatch(uiActions.showLogin(AuthPopupEnum.register))
      return
    }

    const nextFavorite = !isFavorite
    pendingRef.current = true
    setIsUpdating(true)
    dispatch(userActions.updateFavorite(offerId, nextFavorite))
    if (!nextFavorite) Promise.allSettled([removeCachedFavorite(offerId, mutate)])

    try {
      if (nextFavorite) {
        await api.post('/favorite', { offerId })
      } else {
        await api.delete(`/favorite/${offerId}`)
      }
      Promise.allSettled([
        dispatch(userActions.fetchUserFavorites()),
        revalidateFavoriteOffers(mutate),
        revalidateList?.(),
      ])
    } catch (error) {
      if (error.sessionHandled) return
      dispatch(userActions.updateFavorite(offerId, isFavorite))
      Promise.allSettled([revalidateFavoriteOffers(mutate), revalidateList?.()])
      enqueueSnackbar(errorMessage(error), { variant: 'error' })
    } finally {
      pendingRef.current = false
      setIsUpdating(false)
    }
  }, [disabled, dispatch, mutate, enqueueSnackbar, isFavorite, offerId, revalidateList, userDetails])

  return { isFavorite, isUpdating, toggleFavorite }
}

export default useFavoriteOffer
